/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { throwGlobalError } from '~adapters/helpers/error';
import { API_V2_BASE_URL } from '~adapters/helpers/urls';
import { axiosClient } from '~shared/helpers/axios-clients';
import { BranchLikeParameters } from '~shared/types/branch-like';
import { UserBaseShared } from '~shared/types/users';
import { getDirectImportTypeParamValue, getProductionScopeParamValue } from '../helpers/utils';
import {
  DependencyImportTypes,
  DependencyScopes,
  ReleaseDetails,
  ReleaseResponse,
  ReleaseRiskChangelogResponse,
  ReleaseRiskDetails,
  ReleaseRiskQuality,
  ReleaseRiskResponse,
  ReleaseRiskSeverity,
  ReleaseRiskSortBy,
  ReleaseRiskSortOrder,
  ReleaseRiskType,
  RiskStatus,
  RiskTransitions,
} from '../types';

const RELEASES_ENDPOINT = `${API_V2_BASE_URL}/sca/releases`;
const RELEASE_RISKS_ENDPOINT = `${API_V2_BASE_URL}/sca/issues-releases`;
const SBOM_ENDPOINT = `${API_V2_BASE_URL}/sca/sbom-reports`;
const RISK_REPORT_ENDPOINT = `${API_V2_BASE_URL}/sca/risk-reports`;
const DEFAULT_PAGE_SIZE = 100;

export function getReleases({
  importType,
  packageManagers,
  pageSize = DEFAULT_PAGE_SIZE,
  scope,
  ...rest
}: {
  importType?: DependencyImportTypes;
  newlyIntroduced?: true;
  packageManagers?: string[];
  pageIndex?: number;
  pageSize?: number;
  projectKey: string;
  q?: string;
  scope?: DependencyScopes;
} & BranchLikeParameters) {
  const params = {
    direct: getDirectImportTypeParamValue(importType),
    packageManagers: packageManagers?.join(','),
    pageSize,
    productionScope: getProductionScopeParamValue(scope),
    ...rest,
  };
  return axiosClient.get<ReleaseResponse>(RELEASES_ENDPOINT, { params });
}

export function getReleaseDetails(params: { id: string }) {
  return axiosClient.get<ReleaseDetails>(`${RELEASES_ENDPOINT}/${params.id}`);
}

export function getSbomReportBlob({
  branch,
  mediaType,
  component,
  type,
}: {
  branch?: string;
  component: string;
  mediaType: string;
  type: string;
}): Promise<Blob> {
  return axiosClient
    .get<Blob>(SBOM_ENDPOINT, {
      headers: {
        Accept: mediaType,
      },
      params: {
        component,
        type,
        branch,
      },
      responseType: 'blob',
    })
    .catch(throwGlobalError);
}

export function getReleaseRisksBlob({
  branch,
  mediaType,
  component,
  type,
}: {
  branch?: string;
  component: string;
  mediaType: string;
  type: string;
}): Promise<Blob> {
  return axiosClient.get<Blob>(RISK_REPORT_ENDPOINT, {
    headers: {
      Accept: mediaType,
    },
    params: {
      component,
      type,
      branch,
    },
    responseType: 'blob',
  });
}

// This is a special login that is used to represent the unassigned assignee
// It is not a valid login and is unmapped into the assigned=false query param
export const UnassignedLogin = '_';

export function getReleaseRisks({
  importType,
  packageManagers,
  pageSize = DEFAULT_PAGE_SIZE,
  scope,
  ...rest
}: {
  assignees?: string[];
  importType?: DependencyImportTypes;
  newlyIntroduced?: true;
  packageManagers?: string[];
  pageIndex?: number;
  pageSize?: number;
  projectKey: string;
  projects?: string[];
  qualities?: ReleaseRiskQuality[];
  scope?: DependencyScopes;
  severities?: ReleaseRiskSeverity[];
  sort?: `${ReleaseRiskSortOrder}${ReleaseRiskSortBy}`;
  statuses?: RiskStatus[];
  types?: ReleaseRiskType[];
  vulnerabilityId?: string;
} & BranchLikeParameters): Promise<ReleaseRiskResponse> {
  let realAssignees = rest.assignees?.filter((assignee) => assignee !== UnassignedLogin);
  const assigned = rest.assignees?.includes(UnassignedLogin) ? 'false' : undefined;
  if (realAssignees?.length === 0 || assigned) {
    realAssignees = undefined;
  }
  return axiosClient.get<ReleaseRiskResponse>(RELEASE_RISKS_ENDPOINT, {
    params: {
      ...rest,
      assignees: realAssignees?.join(','),
      assigned,
      direct: getDirectImportTypeParamValue(importType),
      packageManagers: packageManagers?.join(','),
      pageSize,
      productionScope: getProductionScopeParamValue(scope),
    },
    paramsSerializer: {
      indexes: null,
    },
  });
}

export function getReleaseRiskDetails(params: { id: string }): Promise<ReleaseRiskDetails> {
  return axiosClient.get<ReleaseRiskDetails>(`${RELEASE_RISKS_ENDPOINT}/${params.id}`);
}

export function getReleaseRiskAssigneeOptions(
  params: {
    projectKey: string;
  } & BranchLikeParameters,
): Promise<UserBaseShared[]> {
  return axiosClient.get<UserBaseShared[]>(`${RELEASE_RISKS_ENDPOINT}/all-assignees`, {
    params,
  });
}

export function updateRiskAssignee(params: {
  assigneeLogin: string | null;
  issueReleaseKey: string;
}) {
  return axiosClient.post<ReleaseRiskDetails>(`${RELEASE_RISKS_ENDPOINT}/update-assignee`, params);
}

export function updateRiskStatus(params: {
  comment?: string;
  issueReleaseKey: string;
  transitionKey: RiskTransitions;
}) {
  return axiosClient.post<ReleaseRiskDetails>(`${RELEASE_RISKS_ENDPOINT}/change-status`, params);
}

interface UpdateSeverityWarningPatch {
  issueReleaseKey: string;
  quality: ReleaseRiskQuality;
  severity: ReleaseRiskSeverity;
}

export function updateRiskSeverity(params: {
  issueReleaseKey: string;
  quality: ReleaseRiskQuality;
  severity: ReleaseRiskSeverity;
}) {
  return axiosClient.patch<UpdateSeverityWarningPatch>(
    `${RELEASE_RISKS_ENDPOINT}/${params.issueReleaseKey}`,
    params,
  );
}

export function getReleaseRiskChangelog(params: { key: string }) {
  return axiosClient.get<ReleaseRiskChangelogResponse>(
    `${RELEASE_RISKS_ENDPOINT}/${params.key}/changelogs`,
  );
}

export function addReleaseRiskComment(params: { comment: string; issueReleaseKey: string }) {
  return axiosClient.post(`${RELEASE_RISKS_ENDPOINT}/comments`, params);
}

export function deleteReleaseRiskComment(params: { commentKey: string; issueReleaseKey: string }) {
  return axiosClient.delete(`${RELEASE_RISKS_ENDPOINT}/${params.issueReleaseKey}/changelog`, {
    params: { issueReleaseChangeKey: params.commentKey },
  });
}

export function editReleaseRiskComment(params: {
  comment: string;
  commentKey: string;
  issueReleaseKey: string;
}) {
  return axiosClient.patch(`${RELEASE_RISKS_ENDPOINT}/${params.issueReleaseKey}/changelog`, {
    issueReleaseChangeKey: params.commentKey,
    comment: params.comment,
  });
}

interface ClearSeverityWarningPatch {
  issueReleaseKey: string;
  severity: ReleaseRiskSeverity | undefined;
}

export function clearSeverityWarning(params: { issueReleaseKey: string }) {
  return axiosClient.patch<ClearSeverityWarningPatch>(
    `${RELEASE_RISKS_ENDPOINT}/${params.issueReleaseKey}`,
    {
      issueReleaseKey: params.issueReleaseKey,
    },
  );
}
